/**
 * Stripe direct Checkout App 配置域 Zod 共享契约（ADM-PAY-S007 / SRV-PAY-029）
 *
 * 契约源：dev-docs `02-API契约/A8-运营支付.md` §9.1~§9.5 + `03-数据字典/70-产品配置.md` §2.1。
 *
 * 边界（A8 §9 开篇 + §9.7 反模式）：本域**只**管每个 App 的 Stripe Account、credential route、固定回跳 URL 与启停。
 * 产品列表、排序、标题、价格、周期、offer 与可售状态继续由 Adapty 管理；Price 路由是
 * `product_metadata.stripe_price_id`（经 A8 §1~§3 端点维护），**本域不存在独立 Price 或场景 slot 管理面**。
 *
 * 秘密边界：`restricted_key` 是 write-only 输入——AES-GCM 加密后写入当前环境 DB route，**明文绝不落 DB、不回显、
 * 不进日志 / audit / trace / 幂等指纹明文**。响应 DTO 因此不含 key、tail、hash、长度，也不含内部 fencing
 * revision `mutation_epoch`（A8 §9.1「响应不得返回 …… 或 `mutation_epoch`」）。
 */

import { z } from 'zod';

/** Stripe Account ID（A8 §9.3）。与 DB CHECK `^acct_[A-Za-z0-9]{1,250}$` 同族，取契约窄值。 */
const StripeAccountIdSchema = z
  .string()
  .regex(/^acct_[A-Za-z0-9]{1,249}$/, 'stripe_account_id must match ^acct_[A-Za-z0-9]{1,249}$');

/**
 * 回跳**基础 URL**（A8 §9.3 / #547）：完整 HTTPS、无空白、**无 fragment、无预置 query**。
 * host 是否属于本 App 的回跳 host 登记、path 是否为固定 path 由服务端裁决（见
 * `lib/payment-return-host-registry.ts` 与 `lib/stripe-checkout-redirect-allowlist.ts`）；此处只挡明显非法形态，
 * 避免把带参 URL 送进写侧。
 *
 * 用字符类拒 `?`/`#`（不用 `URL`）：`packages/shared` tsconfig 无 DOM lib，`new URL` 编不过。
 */
const RedirectUrlSchema = z
  .string()
  .min(1)
  .max(2048)
  .regex(
    /^https:\/\/[^?\s#]+$/,
    'must be a complete HTTPS base URL without whitespace, query, or fragment',
  );

/**
 * restricted key：**首次必填、更新可省略**（省略 = 沿用已存 route）。
 *
 * 形态由 A8 §9.3 钉死为 `^rk_(test|live)_[A-Za-z0-9]{1,240}$` —— 前缀即 Stripe 的 test / live mode，
 * 服务端据此派生 `credential_mode` 并与环境声明比对（不变量 5）。收窄到 `rk_` 同时挡住全权限
 * `sk_*` key：那类 key 派生不出 mode，写进来只会在 serving 侧 fail-closed。
 */
const RestrictedKeySchema = z
  .string()
  .regex(
    /^rk_(test|live)_[A-Za-z0-9]{1,240}$/,
    'restricted_key must match ^rk_(test|live)_[A-Za-z0-9]{1,240}$',
  );

/**
 * PUT /api/v1/integrations/stripe/checkout-configs/:appId 的 body（A8 §9.3）。
 * `.strict()`：未声明键一律拒——误传 `is_active` / `stripe_credential_key_id` / `config_version` 等
 * 服务端管理字段会被静默 strip，运营会以为改了但实际没改（PUT 不通过 body 激活，启停走 §9.5 专用端点）。
 */
export const UpsertStripeCheckoutConfigRequestSchema = z
  .object({
    stripe_account_id: StripeAccountIdSchema,
    /** write-only；首次必填，更新省略即沿用已存 credential route */
    restricted_key: RestrictedKeySchema.optional(),
    success_url: RedirectUrlSchema.nullable().optional(),
    cancel_url: RedirectUrlSchema.nullable().optional(),
  })
  .strict();
export type UpsertStripeCheckoutConfigRequest = z.infer<
  typeof UpsertStripeCheckoutConfigRequestSchema
>;

/** 最近一次连接验证结果（A8 §9.1；credential route / Account / URL 更新后重置为 unverified）。 */
export const STRIPE_CONNECTION_STATUSES = ['unverified', 'connected', 'invalid'] as const;
export const StripeConnectionStatusSchema = z.enum(STRIPE_CONNECTION_STATUSES);
export type StripeConnectionStatus = z.infer<typeof StripeConnectionStatusSchema>;

/**
 * `StripeCheckoutConfigResponse`（A8 §9.1）。
 * **不含**：restricted key / secret tail / hash / 长度 / Stripe 原始响应 / `mutation_epoch`。
 */
export type StripeCheckoutConfigResponse = {
  id: string;
  app_id: string;
  stripe_account_id: string;
  /** 非秘密 credential route id（只作路由，不可解析出 secret 内容）*/
  stripe_credential_key_id: string;
  /** 当前 DB route 的 mode；引用缺行时为 null。 */
  credential_mode: 'test' | 'live' | null;
  /** bigint 序列化为 string（金额外的 bigint 同样走 string，避免 JS number 丢精度）*/
  config_version: string;
  success_url: string | null;
  cancel_url: string | null;
  /** App direct Checkout kill switch */
  is_active: boolean;
  /**
   * 两个回跳 URL 是否均为合法基础 URL：path 命中该字段的**允许集合**
   * （`STRIPE_REDIRECT_PATHS`，今天即 `/payment/checkout/{success,cancel}`）且无 query /
   * fragment（#547/#556 / dd585）。
   *
   * 本校验上线**之前**激活的存量行没经过这道闸：它们可能把 success / cancel 填反或带预置参数且仍
   * `is_active=true`，C 端照常按其创建 Checkout Session —— 付完款落到取消页。写侧与激活侧
   * 已拦新行，存量行只能靠这个标记暴露出来让运营停用后修正。
   *
   * **取全集而非首位**：本字段是**既存行**的读侧结论，改名窗口内旧 path 仍被 C 端放行，报 false
   * 会把一个正在正常收款的配置逼停且激活不回来（见 `STRIPE_REDIRECT_PATHS` 注释）。填反与带参
   * 两类存量缺陷全集口径照样抓得住：success 的允许集里没有 cancel 的 path。
   */
  redirect_path_conforms: boolean;
  connection_status: StripeConnectionStatus;
  /** 最近一次**成功**连接验证时间；失败验证不覆盖本值 */
  last_verified_at: string | null;
  /**
   * 最近一次经 `deactivate` 失活的时刻；`activate` 成功时置 `null`。
   * 与 `updated_at` 语义分离——PUT / activate / test 均刷新 `updated_at`，本列只由 `deactivate` 置位。
   */
  deactivated_at: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * 原生一次性付款证明读取能力的失败项闭集（A8 §9.4）：key 所属 Account 读取或匹配、三类对象读取、
 * 缺已登记适用 Stripe API 版本、测试返回前配置快照已变化。数组次序即响应中 `failed_capabilities` 的排列次序。
 */
export const STRIPE_ONE_TIME_PAYMENT_PROOF_READ_CAPABILITIES = [
  'ACCOUNT',
  'PAYMENT_INTENT',
  'INVOICE_PAYMENT',
  'INVOICE',
  'STRIPE_VERSION',
  'CONFIG_CHANGED',
] as const;
export type StripeOneTimePaymentProofReadCapability =
  (typeof STRIPE_ONE_TIME_PAYMENT_PROOF_READ_CAPABILITIES)[number];

/** `READY` 时 `failed_capabilities` 为空数组，`NOT_READY` 时至少一项 */
export type StripeOneTimePaymentProofRead = {
  status: 'READY' | 'NOT_READY';
  failed_capabilities: StripeOneTimePaymentProofReadCapability[];
};

/** POST /:appId/test 响应（A8 §9.4）。 */
export type StripeCheckoutConnectionTestResponse = {
  app_id: string;
  connection_status: StripeConnectionStatus;
  ready_for_activation: boolean;
  issues: { code: string }[];
  /** IOS/ANDROID 且 Checkout / 恢复验证通过时为对象；WEB 恒为 null。后台按本字段区分就绪，不解析 message */
  one_time_payment_proof_read: StripeOneTimePaymentProofRead | null;
  checked_at: string;
};

/**
 * 回跳 URL 每个字段的**允许 path 集合**（dev-docs `02-API契约/A8-运营支付.md` §9.3 +
 * `01-通用约定/60-环境与域名.md` §2，commit `05d44f5`）—— 全环境一致，admin 配置面只覆盖 host。
 *
 * **数组首位恒为终态 canonical path，其后是迁移窗口内待下线的旧 path**，与 C 端
 * `novel-app-server` `src/lib/stripe/checkout-return-urls.ts` 的 `STRIPE_CHECKOUT_RETURN_PATHS`
 * 同形（该文件的 `usesLegacyStripeCheckoutReturnPath` 用 `indexOf(pathname) > 0` 把「命中旧值」
 * 做成可观测事件，正是靠这个次序）。跨仓那份无法 import，靠契约同源约束。
 *
 * **为什么必须是数组而不是标量**：path 改名时 C 端把新 canonical 放首位、旧 path 留在其后，
 * 该窗口内仍配旧 path 的 App **照常下发**（C 端下发闸是 `allowedPaths.includes(pathname)`）。
 * 本常量若是标量，admin 侧在同一窗口内会红色误报「STRIPE 不会下发」，运营据此去改一个正在
 * 正常收款的配置。今天两侧各只有 1 个元素、`includes` 退化成 `===`，形状差异不出故障 ——
 * 恰恰因此必须现在对齐，否则下次改名时静默失配。
 *
 * **不对称的划法是「新填」vs「既存行」，不是「server」vs「web」**（两侧都各有两类消费方）：
 * - **新填**（server PUT upsert 的入参校验，`isCanonicalStripeRedirectPath`）只认**首位**
 *   —— 运营不得把配置面新填成一个待下线的旧 path，那是在给迁移制造新增量；
 * - **既存行的读与判定**认**全集**（`isAllowedStripeRedirectPath`）：DTO 的
 *   `redirect_path_conforms`、`activate` / `test` 的就绪前置、以及 web 就绪度面板
 *   （`data/adapters/payment-channel-readiness-adapter.ts`）—— 它们复刻的是 C 端下发闸，
 *   判的是「C 端会不会挡」，而 C 端对存量旧 path 在窗口内照常下发。
 *
 * **读侧误取首位的后果是把正在正常收款的配置逼停**：C 端照常下发、照常收款，而 admin 会报
 * `redirect_path_conforms=false`（红色 chip「请先停用后改正」），运营照做停用后，`activate`
 * 走同一道闸抛 path 不符的 `VALIDATION_FAILED` —— 配置再也激活不回来。故读侧一律全集。
 *
 * 为什么放 shared：两侧判的是同一份 path 集合，各写一份字面量则改一侧另一侧静默失配
 * —— 与本文件两条 error prefix 常量同一条理由。
 */
export const STRIPE_REDIRECT_PATHS = {
  success_url: ['/payment/checkout/success'],
  cancel_url: ['/payment/checkout/cancel'],
} as const satisfies Record<string, readonly [string, ...string[]]>;

export type StripeRedirectField = keyof typeof STRIPE_REDIRECT_PATHS;

/** 该字段的**终态 canonical path**（集合首位）—— **新填**（PUT 入参）唯一接受的值。 */
export function canonicalStripeRedirectPath(field: StripeRedirectField): string {
  return STRIPE_REDIRECT_PATHS[field][0];
}

/**
 * 回跳 URL 两类 field 级错误的固定前缀 —— **server 产出、web 消费，必须两侧同源**。
 *
 * 为什么期望值只能走 `errors[].message`：前端 `AdminApiError` 会用 `zhErrorMessage(code)` 覆盖
 * 顶层 message（`redirect_not_allowed` 这个判据到不了 UI，host 未登记与 path 不符共用它），
 * field 级 message 是唯一被原样保留的结构化信号。
 *
 * 为什么放 shared 而不是两边各写一份字面量：前缀是 server 拼、web 按 `startsWith` 切的**协议**，
 * 各写一份则改一侧另一侧静默失配 —— 表现是运营看到降级后的泛化提示，而不是任何测试变红。
 */
export const STRIPE_REDIRECT_PATH_ERROR_PREFIX = 'URL path must be ';

/** 后接清单实际期望值（逗号分隔）。清单非 secret：回跳地址本就公开。 */
export const STRIPE_REDIRECT_ALLOWLIST_ERROR_PREFIX = 'URL must be one of: ';

/**
 * `restricted_key` 与本环境声明的 Stripe mode 不符（A8 §9.3 不变量 5）时的 field 级前缀。
 *
 * 该 field 上有**两种**完全不同的 422：shared Zod 的形态错（`rk_(test|live)_…` 不匹配）、
 * 以及本条 mode 不符。前端若只按 `field === 'restricted_key'` 分支，会把后者渲成
 * 「格式不合法」—— 而 key 的格式明明是对的，运营只会回 Stripe Dashboard 重新生成一把
 * 同 mode 的 key 再试，正是 #387 那 40 分钟的同一失败形态。故给它一个可判别的前缀。
 */
export const STRIPE_CREDENTIAL_MODE_ERROR_PREFIX = 'restricted_key must be a ';
